import type { Position } from "@/types/portfolio"

export interface SafetyDistribution {
	green: number
	amber: number
	red: number
	gray: number
	avgScore: number | null
}

export function computeSafetyDistribution(
	positions: Array<{ ticker: string; exchange: string }>,
	safetyMap: Map<string, { score: number; rating: string }>,
): SafetyDistribution {
	let green = 0,
		amber = 0,
		red = 0,
		gray = 0
	let totalScore = 0,
		scoredCount = 0

	for (const pos of positions) {
		const key = `${pos.ticker}:${pos.exchange}`
		const safety = safetyMap.get(key)
		if (!safety) {
			gray++
			continue
		}
		switch (safety.rating) {
			case "green":
				green++
				break
			case "amber":
				amber++
				break
			case "red":
				red++
				break
			default:
				gray++
				break
		}
		if (safety.rating !== "gray") {
			totalScore += safety.score
			scoredCount++
		}
	}

	return {
		green,
		amber,
		red,
		gray,
		avgScore: scoredCount > 0 ? Math.round(totalScore / scoredCount) : null,
	}
}

export interface PortfolioStats {
	totalValue: number
	annualIncome: number
	portfolioYield: number
	avgDividendGrowth: number
	yieldOnCost: number
}

export function calculatePortfolioStats(
	positions: Position[],
	convert?: (amount: number, fromCurrency?: string) => number,
): PortfolioStats {
	const c = convert ?? ((a: number) => a)
	const totalValue = positions.reduce((sum, p) => sum + c((p.price || 0) * (p.shares || 0), p.currency), 0)
	const annualIncome = positions.reduce(
		(sum, p) => sum + c((p.dividendPerShare || 0) * (p.shares || 0), p.currency),
		0,
	)
	const portfolioYield = totalValue > 0 ? (annualIncome / totalValue) * 100 : 0
	const weightedGrowthSum = positions.reduce((sum, p) => {
		const positionValue = c((p.price || 0) * (p.shares || 0), p.currency)
		return sum + positionValue * (p.dividendGrowth5y || 0)
	}, 0)
	const avgDividendGrowth = totalValue > 0 ? weightedGrowthSum / totalValue : 0
	const totalCostBasis = positions.reduce((sum, p) => sum + c(p.cost_basis || 0, p.currency), 0)
	const yieldOnCost = totalCostBasis > 0 ? (annualIncome / totalCostBasis) * 100 : 0

	return {
		totalValue,
		annualIncome,
		portfolioYield,
		avgDividendGrowth,
		yieldOnCost,
	}
}

export function mergeAggregatePositions(positions: Position[]): Position[] {
	const merged = new Map<string, Position>()

	for (const position of positions) {
		const key = `${position.ticker}:${position.exchange}`
		const existing = merged.get(key)

		if (!existing) {
			merged.set(key, { ...position })
			continue
		}

		existing.shares += position.shares
		if (position.cost_basis != null) {
			existing.cost_basis = (existing.cost_basis || 0) + position.cost_basis
			if (existing.shares > 0) {
				existing.average_price = (existing.cost_basis || 0) / existing.shares
			}
		}
	}

	return Array.from(merged.values())
}
